<template>
  <div class="top-customers-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-s-data"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'top-customers-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return { names: [], times: [], rates: [] }

      const sortedData = [...this.data]
        .sort((a, b) => (b.totalTimes || 0) - (a.totalTimes || 0))
        .slice(0, 10)

      return {
        names: sortedData.map(item => item.customerName || item.customerCode || '未知客户'),
        times: sortedData.map(item => item.totalTimes || 0),
        rates: sortedData.map(item => item.successRate || 0)
      }
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      if (!this.chart || !this.hasData) return

      const { names, times, rates } = this.chartData

      const option = {
        title: {
          text: 'Top10 客户调用排行',
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const dataIndex = params[0].dataIndex
            const customerName = names[dataIndex]
            const timesValue = times[dataIndex]
            const rateValue = rates[dataIndex]

            return `
              <div>
                <strong>${customerName}</strong><br/>
                调用次数: <span style="color: #409EFF">${this.formatNumber(timesValue)}</span><br/>
                成功率: <span style="color: #67C23A">${rateValue}%</span>
              </div>
            `
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: 80,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: names,
          axisLabel: {
            rotate: 45,
            interval: 0,
            fontSize: 11,
            formatter: (value) => {
              return value.length > 6 ? value.substring(0, 6) + '...' : value
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '调用次数',
          axisLabel: {
            formatter: (value) => {
              if (value >= 10000) return (value / 10000).toFixed(1) + '万'
              if (value >= 1000) return (value / 1000).toFixed(1) + 'k'
              return value
            }
          }
        },
        series: [
          {
            name: '调用次数',
            type: 'bar',
            data: times,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#667eea' },
                { offset: 1, color: '#764ba2' }
              ]),
              borderRadius: [4, 4, 0, 0]
            },
            barWidth: '60%',
            label: {
              show: true,
              position: 'top',
              formatter: '{c}',
              fontSize: 10
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.top-customers-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 350px;
  color: #909399;
}

.chart-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}
</style>



// WEBPACK FOOTER //
// src/custom/components/stats/invoice-stats/components/top-customers-chart.vue